import React, { useEffect } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { Skeleton } from 'antd';
import {
  JobDetailsSidebarProps,
  Section,
  STATUS_CONFIG,
} from './JobDetailsSidebarHelpers';
import JobPropertiesModal from './Modals/JobPropertiesModal';
import JobCodeModal from './Modals/JobCodeModal';
import { useGetJobDetail } from 'hooks/jobs';
import HoverActions from 'components/JobsTable/HoverActions/HoverActions';
import useSignedURL from 'hooks/useSignedURL';
import ArcaneMetricsSection from './SparkJobUtilizationGrid/SparkJobUtilizationGrid';
import { JobMetadataOverview } from 'components/reusables/JobMetadataOverview/JobMetadataOverview';
import './JobsDetailsSidebar.scss';

export function JobDetailsSidebar({
  jobId,
  clusterName,
  onClose,
}: JobDetailsSidebarProps) {
  const isOpen = Boolean(jobId);
  const {
    isSignedURL,
    expires,
    signature,
    prefix,
    schemaFromSignedURL: schemaName,
  } = useSignedURL();

  const { data: jobDetails, isLoading: isFetching } = useGetJobDetail({
    schemaName: schemaName ?? '',
    jobId: jobId ?? '',
    isEnabled: !!jobId,
    isSignedURL,
    expires: expires || undefined,
    signature: signature || undefined,
    prefix: prefix || undefined,
    clusterName: clusterName ?? '',
  });

  const [jobPropOpened, setJobPropOpened] = React.useState(false);
  const [jobCodeOpened, setJobCodeOpened] = React.useState(false);

  const statusCfg = jobDetails
    ? STATUS_CONFIG[jobDetails.sparkJobStatus as unknown as JobStatus]
    : null;
  const isDownloadLogsBtnDisabled =
    jobDetails?.sparkJobStatus === JobStatus.In_Queue;

  const onCloseModal = () => {
    onClose();
    setJobPropOpened(false);
    setJobCodeOpened(false);
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      e.key === 'Escape' && onCloseModal();
    };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onCloseModal}
        className={`job-details-sidebar__overlay ${isOpen ? 'job-details-sidebar__overlay--open' : ''}`}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Job details"
        className={`job-details-sidebar__panel ${isOpen ? 'job-details-sidebar__panel--open' : ''}`}>
        <div className="job-details-sidebar__header">
          <div className="job-details-sidebar__header-left">
            <span className="job-details-sidebar__eyebrow">Job Details: </span>
            {isFetching ? (
              <Skeleton.Input
                active
                size="small"
                className="job-details-sidebar__skeleton-title"
              />
            ) : (
              <h2 className="job-details-sidebar__title">
                {jobDetails?.sparkJobId ?? '—'}
              </h2>
            )}
          </div>
          <div className="job-details-sidebar__header-actions">
            <button
              onClick={onCloseModal}
              className="job-details-sidebar__close-btn"
              aria-label="Close">
              <CloseOutlined />
            </button>
          </div>
        </div>

        {!isFetching && statusCfg && jobDetails && (
          <div className="job-details-sidebar__status-row">
            <span
              className={`job-details-sidebar__status-pill ${statusCfg.className}`}>
              {statusCfg.icon}
              <span className="job-details-sidebar__status-text">
                {statusCfg.label}
              </span>
            </span>
          </div>
        )}

        <div className="job-details-sidebar__content">
          {isFetching ? (
            <div className="job-details-sidebar__skeleton">
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
          ) : jobDetails ? (
            <>
              {/* Using DRY structural composition layout */}
              <JobMetadataOverview
                jobDetails={jobDetails}
                showArcaneMetrics={true}
                renderArcaneSection={
                  <ArcaneMetricsSection
                    arcaneMetrics={jobDetails.arcaneMetrics}
                  />
                }
              />
              <div className="job-details-sidebar__divider" />
              <Section title="Actions">
                <HoverActions
                  tableName={jobDetails?.tableName ?? ''}
                  sparkJobID={jobDetails?.sparkJobId ?? '-'}
                  isDownloadLogsBtnDisabled={isDownloadLogsBtnDisabled}
                  clusterUrl={jobDetails?.clusterUrl ?? ''}
                  sparkJobStatus={jobDetails?.sparkJobStatus ?? ''}
                  sparkAppID={jobDetails?.sparkAppId ?? ''}
                  clusterName={jobDetails?.clusterName ?? ''}
                  schemaName={jobDetails?.schemaName ?? ''}
                  clusterPrefix={jobDetails?.clusterPrefix ?? ''}
                  setPropertiesOpen={setJobPropOpened}
                  setCodeOpen={setJobCodeOpened}
                  jobPropOpened={jobPropOpened}
                  jobCodeOpened={jobCodeOpened}
                />
              </Section>
            </>
          ) : (
            <div className="job-details-sidebar__empty">
              No job data available.
            </div>
          )}
        </div>
      </aside>

      <JobPropertiesModal
        job={jobDetails}
        onClose={() => setJobPropOpened(false)}
        isLoading={isFetching}
        clusterName={jobDetails?.clusterName ?? ''}
        isOpen={jobPropOpened}
      />
      <JobCodeModal
        job={jobDetails}
        onClose={() => setJobCodeOpened(false)}
        isLoading={isFetching}
        clusterName={jobDetails?.clusterName ?? ''}
        isOpen={jobCodeOpened}
      />
    </>
  );
}
